import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useState,
} from "react";
import messages from "messages";
// types
import type { Task } from "components/Dashboard/DoctorDashboard/attachments";
import {
  FileEntry,
  OrchidDriveFile,
  OrchidDriveUploadTask,
} from "contexts/orchid-drive/types";
// components
import CancelUploadConfirmModal from "components/Dashboard/DoctorDashboard/attachments/components/cancel-upload-confirm-modal";
import TaskStatus from "components/Dashboard/DoctorDashboard/attachments/components/task-status";
// constants
import {
  TASK_STATUS,
  TASK_TYPES,
} from "components/Dashboard/DoctorDashboard/attachments/constants";
// hooks
import useToggleModal from "hooks/useToggleModal";
import {
  useNoteAttachments,
  useOrchidDriveUploadTasks,
} from "contexts/orchid-drive";
// utils
import renameDuplicate from "components/Dashboard/DoctorDashboard/attachments/utils/renameDuplicate";

type Context = {
  attachments: Record<string, OrchidDriveFile[]>;
  onUpload?: (
    event: ChangeEvent<HTMLInputElement>,
    noteDataItemId: string,
  ) => Promise<void>;
} & Partial<
  Pick<ReturnType<typeof useNoteAttachments>, "uploadTaskAbort" | "deleteEntry">
>;

const BlockAttachmentsContext = createContext<Context>({
  attachments: {},
  uploadTaskAbort: undefined,
  onUpload: undefined,
  deleteEntry: undefined,
});

type Props = {
  attachments: Record<string, OrchidDriveFile[]>;
  children: ReactNode;
  canChange: boolean;
  // pass uploading state to parent
  setIsUploading?: (arg: boolean) => void;
  onError?: (arg: string | Error) => void;
  onChangeSecureData?: Dispatch<SetStateAction<Partial<NoteData> | undefined>>;
  uploadFileNoteAttachment?: (
    files: FileEntry[],
  ) => Promise<void | Map<FileEntry, OrchidDriveUploadTask>>;
} & Partial<
  Pick<ReturnType<typeof useNoteAttachments>, "uploadTaskAbort" | "deleteEntry">
>;

/**
 * attachments context provider
 * @param [patientUserId] use to filter patient
 * @returns
 */
export const BlockAttachmentsContextProvider = ({
  attachments,
  canChange,
  setIsUploading,
  onError,
  uploadFileNoteAttachment,
  uploadTaskAbort,
  deleteEntry,
  onChangeSecureData,
  children,
}: Props) => {
  // TODO: need error by upload task id
  const { uploadTasks, uploadTasksError } = useOrchidDriveUploadTasks();

  useEffect(() => {
    if (uploadTasksError) {
      return onError && onError(messages.oops);
    }
  }, [onError, uploadTasksError]);

  const [watchTasks, setWatchTasks] = useState<
    {
      note_data_item_id?: string;
      task: OrchidDriveUploadTask;
      fileName: string;
      savedToNote?: boolean;
      size: number;
    }[]
  >([]);

  const onUpload = useCallback(
    async (event, note_data_item_id: string) => {
      if (!uploadFileNoteAttachment) {
        return;
      }

      if (!event?.target?.files?.length) {
        return;
      }
      try {
        const nextFiles: FileEntry[] = [];

        for (const file of event.target.files as FileList) {
          const nextName = renameDuplicate(
            file.name,
            Object.values(attachments).flat(),
          );
          nextFiles.push(new FileEntry(file, { name: nextName }));
        }

        const tasksByFile = await uploadFileNoteAttachment(nextFiles);

        if (!tasksByFile) {
          return;
        }

        const createdWatchTasks: typeof watchTasks = [];
        for (const [kFile, vTask] of tasksByFile) {
          createdWatchTasks.push({
            note_data_item_id,
            task: vTask,
            fileName: kFile.name,
            size: kFile.size,
          });
        }

        setWatchTasks((s) => [...s, ...createdWatchTasks]);

        if (setIsUploading) {
          setIsUploading(true);
        }
      } catch {
        return onError && onError(messages.oops);
      }
    },
    [uploadFileNoteAttachment, attachments, setIsUploading, onError],
  );

  // watch task until done
  useEffect(() => {
    if (!watchTasks.length) {
      return;
    }

    const nextAttachedFiles: NoteData["attached_files"] = [];

    for (let index = 0; index < watchTasks.length; index++) {
      const el = watchTasks[index];
      const task = uploadTasks.find(
        (each) => el.task.uploadTaskId === each.uploadTask?.uploadTaskId,
      );

      if (
        !watchTasks[index].savedToNote &&
        task?.uploadTask?.uploadFinalizedDatetime &&
        task?.uploadTask?.uploadTaskId
      ) {
        // callback to save the attachments to note data
        nextAttachedFiles.push(
          ...(task.uploadTask?.fileIds || []).map((eachFile) => ({
            entry_id: eachFile,
            note_data_item_id: el.note_data_item_id,
          })),
        );
        setWatchTasks((s) => [
          ...s.slice(0, index),
          { ...s[index], savedToNote: true },
          ...s.slice(index + 1),
        ]);
      }
    }

    if (watchTasks.every((el) => el.savedToNote)) {
      // when all monitored tasks are complete, notify the parent
      if (setIsUploading) {
        setIsUploading(false);
      }
    }

    return (
      onChangeSecureData &&
      onChangeSecureData((s) => ({
        ...s,
        attached_files: [...(s?.attached_files || []), ...nextAttachedFiles],
      }))
    );
  }, [onChangeSecureData, setIsUploading, uploadTasks, watchTasks]);

  const tasksInView = useMemo(() => {
    const res = uploadTasks.reduce((prev, el) => {
      if (el.uploadTask) {
        const watchTask = watchTasks.find(
          (each) => each.task.uploadTaskId === el.uploadTask?.uploadTaskId,
        );

        if (watchTask) {
          prev.push({
            size: watchTask.size,
            fileName: watchTask.fileName,
            fileIds: el.uploadTask.fileIds,
            id: el.uploadTask.uploadTaskId,
            percent: el.uploadTask.uploadPercent ?? 0,
            busy:
              !el.uploadTask.uploadAbandonedDatetime &&
              !el.uploadTask.uploadFinalizedDatetime,
            state: el.uploadTask.uploadAbandonedDatetime
              ? TASK_STATUS.cancelled
              : el.uploadTask.uploadFinalizedDatetime
              ? TASK_STATUS.complete
              : TASK_STATUS.uploading,
          });
        }
      }

      return prev;
    }, [] as (Pick<NonNullable<typeof uploadTasks[number]["uploadTask"]>, "fileIds"> & Task)[]);

    return res;
  }, [uploadTasks, watchTasks]);

  const onCancelUpload = useCallback(
    async (uploadTask: any) => {
      try {
        if (!uploadTaskAbort || !onChangeSecureData) {
          return;
        }
        await uploadTaskAbort({ uploadTaskId: uploadTask.id });

        setWatchTasks((s) => {
          const index = s.findIndex(
            (el) => el.task.uploadTaskId === uploadTask.id,
          );
          if (index < 0) {
            return s;
          }

          return [...s.slice(0, index), ...s.slice(index + 1)];
        });
      } catch {
        return onError && onError(messages.oops);
      }
    },
    [onChangeSecureData, onError, uploadTaskAbort],
  );

  const onDeleteEntry = useCallback(
    async (arg: Parameters<NonNullable<typeof deleteEntry>>[0]) => {
      try {
        if (!deleteEntry || !onChangeSecureData) {
          return;
        }
        // if delete entry, will abort the upload backend
        await deleteEntry(arg);
        onChangeSecureData((s) => ({
          ...s,
          attached_files: !s?.attached_files
            ? undefined
            : s.attached_files.filter((el) => arg.entryId !== el.entry_id),
        }));
      } catch {
        return onError && onError(messages.oops);
      }
    },
    [deleteEntry, onChangeSecureData, onError],
  );

  const hasBusyTask = useMemo(
    () => tasksInView.some((el) => el.busy),
    [tasksInView],
  );

  const { show, open, close } = useToggleModal(false);

  // remove all tasks in view, should consider if the task busy state
  const onCloseUploads = useCallback(async () => {
    try {
      if (!uploadTaskAbort || !onChangeSecureData) {
        return;
      }

      await Promise.all(
        tasksInView.map(async (uploadTask) => {
          try {
            if (uploadTask.percent !== 100) {
              await uploadTaskAbort({ uploadTaskId: uploadTask.id });
            }

            setWatchTasks((s) => {
              const index = s.findIndex(
                (el) => el.task.uploadTaskId === uploadTask.id,
              );
              if (index < 0) {
                return s;
              }

              return [...s.slice(0, index), ...s.slice(index + 1)];
            });

            return Promise.resolve();
          } catch (error) {
            return Promise.reject(error);
          }
        }),
      );
    } catch {
      onError && onError(messages.oops);
    } finally {
      close();
    }
  }, [close, onChangeSecureData, onError, tasksInView, uploadTaskAbort]);

  return (
    <>
      <BlockAttachmentsContext.Provider
        value={{
          onUpload: !canChange ? undefined : onUpload,
          deleteEntry: !canChange ? undefined : onDeleteEntry,
          uploadTaskAbort: !canChange ? undefined : uploadTaskAbort,
          attachments,
        }}
      >
        {children}
        {tasksInView.length > 0 && (
          <TaskStatus
            variant={TASK_TYPES.upload}
            tasks={tasksInView}
            // TODO: go with the UX for now, show if has task
            show={true}
            onHide={() => (hasBusyTask ? open() : onCloseUploads())}
            onCancel={(v) => onCancelUpload(v as any)}
          />
        )}
      </BlockAttachmentsContext.Provider>
      {/* confirm remove all tasks, include those in progress */}
      <CancelUploadConfirmModal
        show={show}
        onCancel={close}
        onConfirm={onCloseUploads}
      />
    </>
  );
};

export const useBlockAttachments = () => useContext(BlockAttachmentsContext);
